<script setup lang="ts">
import { useSectionObserver } from '~/composables/useSectionObserver';

const props = defineProps<{
  sections: {
    sectionHeading: string;
    slug: string;
  }[];

  featuredPrograms?: {
    // node: {
    id: string;
    link?: string;
    slug: string;
    uri: string;
    title: string;
    // };
  }[];

  activeSection?: string | null;
  scrollToSection?: (slug: string) => void;
  hideTitle?: boolean;
}>();

/**
 * Only initialize section observer if the parent DID NOT provide
 * its own activeSection / scrollToSection.
 *
 * This avoids the error where IntersectionObserver runs on SSR AND
 * avoids overriding observer behavior on pages that supply their own logic.
 */
let internalActiveSection: Ref<string | null> | null = null;
let internalScrollToSection: ((slug: string) => void) | null = null;

if (!props.activeSection && !props.scrollToSection) {
  const observer = useSectionObserver(props.sections, {
    scrollOffset: 20,
    rootMarginTop: '-20%',
    rootMarginBottom: '-5%',
  });

  internalActiveSection = observer.activeSection;
  internalScrollToSection = observer.scrollToSection;
}

/** Use provided values if available; otherwise use internal observer */
const active = computed(() => props.activeSection ?? internalActiveSection?.value ?? null);
const scrollTo = (slug: string) => {
  if (props.scrollToSection) return props.scrollToSection(slug);
  if (internalScrollToSection) return internalScrollToSection(slug);
  // fallback: allow default anchor jump
};
</script>

<template>
  <div class="bg-brand-50 top-4 z-10 hidden rounded-2xl p-8 lg:sticky lg:block">
    <!-- Table of Contents -->
    <div v-if="sections.length">
      <TypographyTitle v-if="!hideTitle" size="sm" as="h5" class="mb-4 normal-case">
        TABLE OF CONTENTS:
      </TypographyTitle>

      <ul class="border-accent-500 mb-8 ml-1 border-l-2 pl-6">
        <li
          v-for="section in sections"
          :key="section.slug"
          :class="['text-13 not-last-of-type:mb-[9px]', active === section.slug ? 'active' : '']"
        >
          <a :href="'#' + section.slug" class="text-brand-600" @click.prevent="scrollTo(section.slug)">
            {{ section.sectionHeading }}
          </a>
        </li>
      </ul>
    </div>

    <!-- Featured Programs -->
    <div v-if="featuredPrograms?.length">
      <TypographyTitle size="sm" as="h5" class="mb-4 normal-case"> Programs Featured </TypographyTitle>

      <ul class="ml-4 list-disc">
        <li v-for="program in featuredPrograms" :key="program.id" class="text-13 marker:text-accent-500 mb-3">
          <a :href="withTrailingSlash(program.uri)" class="text-brand-600 capitalize underline">
            <!-- TO DO: this is currently using the progam slug, we need to grab the title -->
            {{ program.title }}
            <span v-if="program.uri.split('/')[2]">
              ({{ program.uri.split('/')[2] }}s{{ program.uri.split('/')[4] ? ' ' + program.uri.split('/')[4] : '' }})
            </span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>

<style scoped>
li.active {
  position: relative;
}
li.active a {
  font-weight: bold;
}
li.active::before {
  content: '';
  background-color: var(--color-brand-50);
  width: var(--text-13);
  height: 100%;
  min-height: 40px;
  display: block;
  position: absolute;
  left: -26px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  transition: width 0.3s ease;
}
li.active::after {
  content: '';
  background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 13 15' fill='none'%3E%3Cpath d='M12.2617 7.37744L0.261718 14.3056L0.261719 0.449238L12.2617 7.37744Z' fill='%23DCAE3A'/%3E%3C/svg%3E");
  width: 13px;
  height: 15px;
  position: absolute;
  left: -30px;
  top: 50%;
  transform: translateY(-65%);
  z-index: 2;
}
</style>
